//#region IMPORT
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
/** Services Import */
/** Components */
import M_EIC_NEW from './M_EIC_NEW';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import useFcmHandler from '../../../../Hooks/useFcmHandler';
import { useSearch2, useSearch3, useSave1 } from './M_EIC_NEW_Query';
import { useSave } from '../../../../Hooks/useSave';
import { M_EIC_NEW_Schema } from './M_EIC_NEW_Schema';
//#endregion

function M_EIC_NEWF() {
  //#region 변수
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const searchInfo = useSelector((state) => state.searchInfo);

  const depList = [{ value: '', name: '전체' }, ...searchInfo.depList];
  const depName =
    depList.find((d) => (d.value ?? '') === (searchInfo.depCd ?? ''))?.name ??
    '전체';

  const dispatch = useDispatch();
  const [equipList, setEquipList] = useState([]); // 사업장별 보호구 리스트
  const [employeeList, setEmployeeList] = useState([]); // 근로자 리스트
  const [selectedEmployees, setSelectedEmployees] = useState([]); // 선택된 근로자 목록 (다중)
  const [selectedEquips, setSelectedEquips] = useState([]); // 선택된 보호구 리스트
  const [equipInputs, setEquipInputs] = useState({}); // 각 보호구별 입력값 (qty, reason, remark)

  // 보호구 목록을 마지막으로 로드한 부서코드 추적 (불필요한 재조회 방지)
  const loadedDepCdRef = useRef(null);

  const sendFcm = useFcmHandler();
  //#endregion

  //#region 나머지함수
  const Init = async () => {
    try {
      await Search2Async({ custCd: userInfo.custCd ?? '' });
    } catch (e) {
      console.log('Error: ', e);
      dispatch(
        showSnackbar({
          message: e.message,
          type: 'error',
        })
      );
    }
  };

  useEffect(() => {
    Init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 부서 필터 변경 시 선택 초기화
  useEffect(() => {
    setSelectedEmployees([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInfo.depCd]);

  useEffect(() => {
    if (selectedEmployees.length === 0) {
      setEquipList([]);
      setSelectedEquips([]);
      setEquipInputs({});
      loadedDepCdRef.current = null;
    } else {
      const currentDepCd = selectedEmployees[0]?.depCd ?? '';
      // 부서가 바뀐 경우에만 보호구 목록 재조회
      if (currentDepCd !== loadedDepCdRef.current) {
        loadedDepCdRef.current = currentDepCd;
        Search3Async({ custCd: userInfo.custCd ?? '' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployees]);
  //#endregion

  //#region 쿼리
  const { runAsync: Search2Async } = useSearch2({
    onSuccess: (rows) => {
      setEmployeeList(rows);
    },
  });
  const { runAsync: Search3Async } = useSearch3({
    onSuccess: (rows) => {
      const firstDepCd = selectedEmployees[0]?.depCd ?? '';
      setEquipList(
        rows.filter((item) => item.managerDepartments.includes(firstDepCd))
      );
    },
  });
  const { runAsync: Save1Async } = useSave1();

  const { run: Save1 } = useSave({
    menuId: 'M_EIC_NEW',
    schema: M_EIC_NEW_Schema,
    saveData: () =>
      selectedEmployees.flatMap((employee) =>
        equipList
          .filter((equip) => {
            const equipId = equip.erCd;
            return equipId && selectedEquips.includes(equipId);
          })
          .map((equip) => {
            const equipId = equip.erCd;
            const inputs = equipInputs[equipId] || {};
            return {
              erCd: equip.erCd,
              equipNm: equip.equipNm,
              custCd: searchInfo.custCd,
              toUserId: employee.id,
              qty: inputs.qty || '',
              reason: inputs.reason || '',
              remark: inputs.remark || '',
            };
          })
      ),
    requestSave: (rows) => Save1Async(rows),
    onSuccess: () => {
      setSelectedEquips([]);
      setEquipInputs({});

      // 보호구 재고 갱신
      loadedDepCdRef.current = null;
      Search3Async({ custCd: userInfo.custCd ?? '' });

      // 선택된 근로자 전원에게 FCM 발송
      selectedEmployees.forEach((employee) => {
        sendFcm({
          targetUserId: employee.id,
          requestUserId: userInfo.userid,
          body: `보호구가 지급되었습니다. 확인해주세요.`,
          status: '보호구지급',
          codeGroup: 'AL_008',
          title: '보호구 지급',
          route: '/M_Equipment',
          responseData: '',
        });
      });
    },
  });
  //#endregion

  //#region 이벤트 핸들러
  /** 단일 선택 (부서 필터 전체일 때) */
  const HandleEmployeeSingleSelect = (employee) => {
    setSelectedEmployees((prev) => {
      const isAlreadySelected = prev.length === 1 && prev[0].id === employee.id;
      return isAlreadySelected ? [] : [employee];
    });
  };

  /** 다중 선택 토글 (특정 부서 필터일 때) */
  const HandleEmployeeToggle = (employee) => {
    setSelectedEmployees((prev) => {
      const exists = prev.find((e) => e.id === employee.id);
      if (exists) return prev.filter((e) => e.id !== employee.id);
      return [...prev, employee];
    });
  };

  const HandleSelectAll = (filteredList) => {
    setSelectedEmployees(filteredList);
  };

  const HandleDeselectAll = () => {
    setSelectedEmployees([]);
  };

  const HandleEquipToggle = (equipId) => {
    if (!equipId || equipId === '') return;

    setSelectedEquips((prev) => {
      const index = prev.findIndex((id) => id === equipId);
      if (index >= 0) {
        setEquipInputs((prevInputs) => {
          const newInputs = { ...prevInputs };
          delete newInputs[equipId];
          return newInputs;
        });
        return prev.filter((id) => id !== equipId);
      } else {
        setEquipInputs((prevInputs) => ({
          ...prevInputs,
          [equipId]: {
            qty: 1,
            reason: '',
            remark: '',
          },
        }));
        return [...prev, equipId];
      }
    });
  };

  const HandleInputChange = (equipId, field, value) => {
    setEquipInputs((prev) => ({
      ...prev,
      [equipId]: {
        ...prev[equipId],
        [field]: value,
      },
    }));
  };
  //#endregion

  return (
    <div className="wrap">
      <M_EIC_NEW
        userInfo={userInfo}
        equipList={equipList}
        employeeList={employeeList}
        selectedEmployees={selectedEmployees}
        selectedEquips={selectedEquips}
        equipInputs={equipInputs}
        onEmployeeSingleSelect={HandleEmployeeSingleSelect}
        onEmployeeToggle={HandleEmployeeToggle}
        onSelectAll={HandleSelectAll}
        onDeselectAll={HandleDeselectAll}
        onEquipToggle={HandleEquipToggle}
        onInputChange={HandleInputChange}
        onSave={Save1}
        filterState={{
          depName: depName,
          depCd: searchInfo.depCd ?? '',
        }}
        depList={depList}
      />
    </div>
  );
}

export default M_EIC_NEWF;
